import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';

interface Genre {
  id: string;
  name: string;
}

const CONTENT_TYPES = [
  { value: 'movie', label: 'Movie' },
  { value: 'short_film', label: 'Short Film' },
  { value: 'documentary', label: 'Documentary' },
] as const;

const RATINGS = ['G', 'PG', 'PG-13', 'R', 'NC-17'] as const;

export default function CreateMovie() {
  const { publisher } = useAuth();
  const navigate = useNavigate();

  const [genres, setGenres] = useState<Genre[]>([]);
  const [selectedGenreIds, setSelectedGenreIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState('');
  const [contentType, setContentType] = useState<'movie' | 'short_film' | 'documentary'>('movie');
  const [description, setDescription] = useState('');
  const [rating, setRating] = useState('');
  const [trailerUrl, setTrailerUrl] = useState('');
  const [posterFile, setPosterFile] = useState<File | null>(null);

  useEffect(() => {
    supabase.from('genres').select('id, name').order('name').then(({ data }: { data: Genre[] | null }) => {
      if (data) setGenres(data);
    });
  }, []);

  const toggleGenre = (id: string) => {
    setSelectedGenreIds(prev =>
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!publisher?.id) return;
    if (!title.trim()) { toast.error('Title is required'); return; }

    setSubmitting(true);
    try {
      let posterUrl: string | null = null;

      if (posterFile) {
        const ext = posterFile.name.split('.').pop();
        const path = `${publisher.id}/works/${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('publisher-assets')
          .upload(path, posterFile, { upsert: true });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage
          .from('publisher-assets')
          .getPublicUrl(path);
        posterUrl = urlData.publicUrl;
      }

      const { data: work, error: workError } = await supabase
        .from('works')
        .insert({
          publisher_id: publisher.id,
          title: title.trim(),
          description: description.trim() || null,
          type: contentType,
          status: 'draft',
          poster_url: posterUrl,
          trailer_video_url: trailerUrl.trim() || null,
        })
        .select('id')
        .single();

      if (workError) throw workError;

      if (selectedGenreIds.length > 0) {
        await supabase.from('work_genres').insert(
          selectedGenreIds.map(genre_id => ({ work_id: work.id, genre_id }))
        );
      }

      toast.success('Title created — now attach a video to publish it.');
      navigate(`/content/movies/${work.id}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create title');
    } finally {
      setSubmitting(false);
    }
  };

  const contentTypeLabel = CONTENT_TYPES.find(t => t.value === contentType)?.label ?? 'Movie';

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/content/movies')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold">New Title</h1>
          <p className="text-muted-foreground mt-1">
            Create the title first — you'll attach a video after
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="partner-card">
          <CardHeader>
            <CardTitle>Title Details</CardTitle>
            <CardDescription>Basic information about your {contentTypeLabel.toLowerCase()}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="content-type">Content Type *</Label>
              <Select value={contentType} onValueChange={(v) => setContentType(v as typeof contentType)}>
                <SelectTrigger id="content-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={`${contentTypeLabel} title`}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Synopsis or description"
                rows={4}
                className="resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rating">Rating</Label>
              <Select value={rating} onValueChange={setRating}>
                <SelectTrigger id="rating">
                  <SelectValue placeholder="Select a rating" />
                </SelectTrigger>
                <SelectContent>
                  {RATINGS.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="poster">Poster Image</Label>
              <Input
                id="poster"
                type="file"
                accept="image/*"
                onChange={e => setPosterFile(e.target.files?.[0] ?? null)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="trailer">Trailer URL (optional)</Label>
              <Input
                id="trailer"
                value={trailerUrl}
                onChange={e => setTrailerUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
          </CardContent>
        </Card>

        <Card className="partner-card">
          <CardHeader>
            <CardTitle>Genres</CardTitle>
            <CardDescription>Select all genres that apply</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {genres.map(genre => (
                <Badge
                  key={genre.id}
                  variant={selectedGenreIds.includes(genre.id) ? 'default' : 'outline'}
                  className={`cursor-pointer transition-colors ${
                    selectedGenreIds.includes(genre.id)
                      ? 'bg-primary hover:bg-primary/80'
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => toggleGenre(genre.id)}
                >
                  {genre.name}
                </Badge>
              ))}
            </div>
            {selectedGenreIds.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                {selectedGenreIds.length} genre{selectedGenreIds.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/content/movies')}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Title'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
